import React, { useState } from "react";
import { Alert, Button, Card, Col, Descriptions, Form, Input, Modal, Row, Space, Typography, message } from "antd";
import { CrownOutlined, CustomerServiceOutlined, DeleteOutlined, LockOutlined, SafetyOutlined, UserOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { changePassword, requestAccountDeletion } from "../services/api";

const { Title, Text } = Typography;

const roleNames = {
    admin: "管理员",
    landlord: "房东",
    sub_account: "子账号",
    agent: "中介",
    user: "普通用户"
};

const ProfileScreen = ({ user }) => {
    const navigate = useNavigate();
    const [passwordOpen, setPasswordOpen] = useState(false);
    const [deletionOpen, setDeletionOpen] = useState(false);
    const [changingPassword, setChangingPassword] = useState(false);
    const [deletingAccount, setDeletingAccount] = useState(false);
    const [passwordForm] = Form.useForm();
    const [deletionForm] = Form.useForm();
    const canDeleteAccount = user?.role !== "admin" && user?.role !== "sub_account";

    const openCustomerService = () => {
        navigate(user?.role === "admin" ? "/messages" : "/messages?contact=admin");
    };

    const handleChangePassword = async () => {
        try {
            const values = await passwordForm.validateFields();
            setChangingPassword(true);
            await changePassword({ current_password: values.current_password, new_password: values.new_password });
            message.success("密码修改成功，请妥善保管新密码");
            setPasswordOpen(false);
            passwordForm.resetFields();
        } catch (error) {
            if (!error?.errorFields) message.error(error?.error || "密码修改失败，请重试");
        } finally {
            setChangingPassword(false);
        }
    };

    const handleAccountDeletion = async () => {
        try {
            const values = await deletionForm.validateFields();
            setDeletingAccount(true);
            await requestAccountDeletion(values);
            localStorage.removeItem("token");
            localStorage.removeItem("user");
            message.success("注销申请已提交，账号已安全退出");
            navigate("/login");
        } catch (error) {
            if (!error?.errorFields) message.error(error?.error || "注销申请提交失败");
        } finally {
            setDeletingAccount(false);
        }
    };

    return (
        <div className="profile-page">
            <div className="profile-page-heading">
                <Title level={2}><UserOutlined /> 个人中心</Title>
                <Text type="secondary">查看账号资料、管理账号安全并联系平台客服</Text>
            </div>

            <Row gutter={[16, 16]}>
                <Col xs={24} lg={14}>
                    <Card title="账号资料" className="profile-page-card">
                        <Descriptions column={1} bordered size="middle">
                            <Descriptions.Item label="用户名">{user?.username || "-"}</Descriptions.Item>
                            <Descriptions.Item label="账号身份">{roleNames[user?.role] || "普通用户"}</Descriptions.Item>
                            <Descriptions.Item label="手机号">{user?.phone || "未填写"}</Descriptions.Item>
                        </Descriptions>
                    </Card>
                </Col>
                <Col xs={24} lg={10}>
                    <Card title={<span><SafetyOutlined /> 账号与安全</span>} className="profile-page-card">
                        <Space direction="vertical" size={12} style={{ width: "100%" }}>
                            <Button icon={<LockOutlined />} size="large" block onClick={() => { passwordForm.resetFields(); setPasswordOpen(true); }}>
                                修改密码
                            </Button>
                            {canDeleteAccount ? (
                                <Button danger icon={<DeleteOutlined />} size="large" block onClick={() => { deletionForm.resetFields(); setDeletionOpen(true); }}>
                                    申请注销账号
                                </Button>
                            ) : (
                                <Text type="secondary" style={{ fontSize: 12, textAlign: "center" }}>
                                    {user?.role === "admin" ? "管理员账号不能自助注销" : "子账号由房东主账号负责管理"}
                                </Text>
                            )}
                        </Space>
                    </Card>
                </Col>
                {user?.role === "user" && (
                    <Col span={24}>
                        <Card title={<span><CrownOutlined /> 升级账号</span>} className="profile-page-card profile-upgrade-card">
                            <div>
                                <div className="profile-service-title">升级为房东或中介</div>
                                <Text type="secondary">选择管理员已上架的会员方案并提交申请，审核通过后身份才会生效。</Text>
                            </div>
                            <Button type="primary" icon={<CrownOutlined />} size="large" onClick={() => navigate("/membership")}>选择升级身份</Button>
                        </Card>
                    </Col>
                )}
                <Col span={24}>
                    <Card title={<span><CustomerServiceOutlined /> 联系客服</span>} className="profile-page-card profile-service-card">
                        <div>
                            <div className="profile-service-title">{user?.role === "admin" ? "处理客服消息" : "需要平台协助？"}</div>
                            <Text type="secondary">
                                {user?.role === "admin" ? "管理员账号同时也是平台客服账号，可在消息中心回复用户。" : "点击后将直接进入与平台管理员账号的客服对话。"}
                            </Text>
                        </div>
                        <Button type="primary" icon={<CustomerServiceOutlined />} size="large" onClick={openCustomerService}>
                            {user?.role === "admin" ? "进入客服消息" : "联系客服"}
                        </Button>
                    </Card>
                </Col>
            </Row>

            <Modal
                className="password-modal"
                title={<span><LockOutlined style={{ color: "#1677ff", marginRight: 8 }} />修改账号密码</span>}
                open={passwordOpen}
                width={420}
                centered
                destroyOnClose
                maskClosable={false}
                onCancel={() => { setPasswordOpen(false); passwordForm.resetFields(); }}
                footer={[
                    <Button key="cancel" onClick={() => { setPasswordOpen(false); passwordForm.resetFields(); }}>取消</Button>,
                    <Button key="submit" type="primary" loading={changingPassword} onClick={handleChangePassword}>确认修改</Button>
                ]}
            >
                <Alert message="为保护账号安全，请先验证当前密码。新密码至少8位，并同时包含字母和数字。" type="info" showIcon style={{ marginBottom: 18 }} />
                <Form form={passwordForm} layout="vertical" requiredMark={false}>
                    <Form.Item name="current_password" label="当前密码" rules={[{ required: true, message: "请输入当前密码" }]}>
                        <Input.Password autoComplete="current-password" placeholder="请输入当前使用的密码" size="large" />
                    </Form.Item>
                    <Form.Item name="new_password" label="新密码" rules={[
                        { required: true, message: "请输入新密码" },
                        { min: 8, message: "新密码至少8位" },
                        { max: 64, message: "新密码不能超过64位" },
                        { pattern: /^(?=.*[A-Za-z])(?=.*\d).+$/, message: "新密码必须同时包含字母和数字" }
                    ]}>
                        <Input.Password autoComplete="new-password" placeholder="至少8位，包含字母和数字" size="large" />
                    </Form.Item>
                    <Form.Item
                        name="confirm_password"
                        label="再次输入新密码"
                        dependencies={["new_password"]}
                        rules={[
                            { required: true, message: "请再次输入新密码" },
                            ({ getFieldValue }) => ({ validator(_, value) { return !value || getFieldValue("new_password") === value ? Promise.resolve() : Promise.reject(new Error("两次输入的新密码不一致")); } })
                        ]}
                    >
                        <Input.Password autoComplete="new-password" placeholder="请再次输入新密码" size="large" onPressEnter={handleChangePassword} />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={<span><DeleteOutlined style={{ color: "#ff4d4f", marginRight: 8 }} />申请注销账号</span>}
                open={deletionOpen}
                width={460}
                centered
                maskClosable={false}
                onCancel={() => { setDeletionOpen(false); deletionForm.resetFields(); }}
                footer={[
                    <Button key="cancel" onClick={() => setDeletionOpen(false)}>暂不注销</Button>,
                    <Button key="submit" danger type="primary" loading={deletingAccount} onClick={handleAccountDeletion}>确认提交注销申请</Button>
                ]}
            >
                <Alert message="提交后账号会立即停用。若仍有在租房间，请先处理完退租关系。" type="warning" showIcon style={{ marginBottom: 18 }} />
                <Form form={deletionForm} layout="vertical" requiredMark={false}>
                    <Form.Item name="current_password" label="当前密码" rules={[{ required: true, message: "请输入当前密码" }]}>
                        <Input.Password size="large" placeholder="用于确认是本人操作" />
                    </Form.Item>
                    <Form.Item name="confirm_text" label="请输入：确认注销" rules={[
                        { required: true, message: "请输入“确认注销”" },
                        { validator: (_, value) => value === "确认注销" ? Promise.resolve() : Promise.reject(new Error("请输入完整的“确认注销”")) }
                    ]}>
                        <Input size="large" placeholder="确认注销" onPressEnter={handleAccountDeletion} />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default ProfileScreen;
